<template>
  <span v-if="tolerancesEnabled && value"> ± {{ value }}" </span>
</template>

<script>
export default {
  props: {
    dimensionName: {
      type: String,
      default: ""
    },
    dimensionValue: {
      type: Number,
      default: null
    },
    productName: {
      type: String,
      default: ""
    },
    value: {
      type: Number,
      default: null
    },
    tolerancesEnabled: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    dimensionValue() {
      this.changeHandler();
    },
    tolerancesEnabled() {
      this.changeHandler();
    }
  },
  methods: {
    changeHandler() {
      let result;

      if (this.tolerancesEnabled) {
        result = this.determineTolerance(
          this.productName,
          this.dimensionName,
          this.dimensionValue
        );
      } else if (this.dimensionName === "Wall Thickness") {
        result = 1;
      } else {
        result = 0;
      }

      this.$emit("input", result);
    },
    determineTolerance(productName, dimensionName, dimensionValue) {
      switch (productName) {
        case "Round Bar":
          return this.roundDimensionTolerance(dimensionName, dimensionValue);
        case "Wire":
          return this.roundDimensionTolerance(dimensionName, dimensionValue);
        case "Hex Bar":
          return this.hexDimensionTolerance(dimensionName, dimensionValue);
        case "Block":
          return this.blockDimensionTolerance(dimensionName, dimensionValue);
        case "Plate":
          return this.plateDimensionTolerance(dimensionName, dimensionValue);
        case "Rectangular Bar":
          return this.rectangularBarDimensionTolerance(
            dimensionName,
            dimensionValue
          );
        case "Sheet":
          return this.sheetDimensionTolerance(dimensionName, dimensionValue);
        case "Hollow Bar":
          return this.hollowBarTolerance(dimensionName, dimensionValue);
        case "Seamless Pipe":
          return this.pipeTolerance(dimensionName, dimensionValue);
        case "Welded Pipe":
          return this.pipeTolerance(dimensionName, dimensionValue);
        case "Seamless Tube":
          return this.tubingTolerance(dimensionName, dimensionValue);
        case "Welded Tube":
          return this.tubingTolerance(dimensionName, dimensionValue);
        default:
          return 0;
      }
    },
    roundDimensionTolerance(dimensionName, dimensionValue) {
      if (dimensionName === "Diameter") {
        switch (true) {
          case dimensionValue <= 1:
            return 0.002;
          case dimensionValue <= 1.5:
            return 0.0025;
          case dimensionValue <= 4:
            return 0.003;
          case dimensionValue <= 6:
            return 0.031;
          case dimensionValue <= 9:
            return 0.062;
          case dimensionValue > 9:
            return 0.125;
        }
      } else if (dimensionName === "Length") {
        switch (true) {
          case dimensionValue <= 0.5:
            return 0.05;
          case dimensionValue <= 0.75:
            return 0.062;
          case dimensionValue <= 1:
            return 0.078;
          case dimensionValue <= 3:
            return 0.093;
          case dimensionValue <= 4:
            return 0.1;
          case dimensionValue > 4:
            return 0.125;
        }
      }
    },
    hexDimensionTolerance(dimensionName, dimensionValue) {
      if (dimensionName === "Across Flats Hex Dimension") {
        switch (true) {
          case dimensionValue <= 0.25:
            return 0.0025;
          case dimensionValue <= 0.5:
            return 0.007;
          case dimensionValue <= 1:
            return 0.01;
          case dimensionValue <= 1.5:
            return 0.021;
          case dimensionValue <= 2:
            return 0.031;
          case dimensionValue <= 2.5:
            return 0.047;
          case dimensionValue > 2.5:
            return 0.062;
        }
      } else if (dimensionName === "Length") {
        switch (true) {
          case dimensionValue <= 0.5:
            return 0.05;
          case dimensionValue <= 0.75:
            return 0.062;
          case dimensionValue <= 1:
            return 0.078;
          case dimensionValue <= 3:
            return 0.093;
          case dimensionValue <= 4:
            return 0.1;
          case dimensionValue > 4:
            return 0.125;
        }
      }
    },
    blockDimensionTolerance(dimensionName, dimensionValue) {
      if (dimensionName === "Thickness") {
        switch (true) {
          case dimensionValue <= 4:
            return 0.002;
          case dimensionValue <= 6:
            return 0.003;
          case dimensionValue <= 9:
            return 0.004;
          case dimensionValue > 9:
            return 1.5;
        }
      } else if (dimensionName === "Width" || dimensionName === "Length") {
        switch (true) {
          case dimensionValue <= 0.5:
            return 0.05;
          case dimensionValue <= 0.75:
            return 0.062;
          case dimensionValue <= 1:
            return 0.078;
          case dimensionValue <= 3:
            return 0.093;
          case dimensionValue <= 4:
            return 0.1;
          case dimensionValue > 4:
            return 0.125;
        }
      }
    },
    plateDimensionTolerance(dimensionName, dimensionValue) {
      if (dimensionName === "Thickness") {
        switch (true) {
          case dimensionValue <= 0.375:
            return 0.045;
          case dimensionValue <= 0.75:
            return 0.055;
          case dimensionValue <= 1:
            return 0.06;
          case dimensionValue <= 2:
            return 0.07;
          case dimensionValue <= 3:
            return 0.125;
          case dimensionValue <= 4:
            return 0.175;
          case dimensionValue <= 6:
            return 0.25;
          case dimensionValue <= 8:
            return 0.35;
          case dimensionValue > 8:
            return 0.45;
        }
      } else if (dimensionName === "Width" || dimensionName === "Length") {
        switch (true) {
          case dimensionValue <= 0.5:
            return 0.05;
          case dimensionValue <= 0.75:
            return 0.062;
          case dimensionValue <= 1:
            return 0.078;
          case dimensionValue <= 3:
            return 0.093;
          case dimensionValue <= 4:
            return 0.01;
          case dimensionValue > 4:
            return 0.125;
        }
      }
    },
    rectangularBarDimensionTolerance(dimensionName, dimensionValue) {
      if (dimensionName === "Thickness") {
        switch (true) {
          case dimensionValue <= 1:
            return 0.015;
          case dimensionValue <= 2:
            return 0.031;
          case dimensionValue <= 4:
            return 0.062;
          case dimensionValue <= 6:
            return 0.093;
          case dimensionValue <= 8:
            return 0.125;
          case dimensionValue > 8:
            return 0.156;
        }
      } else if (dimensionName === "Width" || dimensionName === "Length") {
        switch (true) {
          case dimensionValue <= 0.5:
            return 0.05;
          case dimensionValue <= 0.75:
            return 0.062;
          case dimensionValue <= 1:
            return 0.078;
          case dimensionValue <= 3:
            return 0.093;
          case dimensionValue <= 4:
            return 0.01;
          case dimensionValue > 4:
            return 0.125;
        }
      }
    },
    sheetDimensionTolerance(dimensionName, dimensionValue) {
      if (dimensionName === "Thickness") {
        switch (true) {
          case dimensionValue <= 0.005:
            return 0.001;
          case dimensionValue <= 0.007:
            return 0.0015;
          case dimensionValue <= 0.016:
            return 0.002;
          case dimensionValue <= 0.026:
            return 0.003;
          case dimensionValue <= 0.058:
            return 0.004;
          case dimensionValue <= 0.083:
            return 0.005;
          case dimensionValue <= 0.098:
            return 0.006;
          case dimensionValue <= 0.114:
            return 0.007;
          case dimensionValue <= 0.13:
            return 0.008;
          case dimensionValue <= 0.145:
            return 0.01;
          case dimensionValue > 0.145:
            return 0.012;
        }
      } else if (dimensionName === "Width" || dimensionName === "Length") {
        switch (true) {
          case dimensionValue <= 0.5:
            return 0.05;
          case dimensionValue <= 0.75:
            return 0.062;
          case dimensionValue <= 1:
            return 0.078;
          case dimensionValue <= 3:
            return 0.093;
          case dimensionValue <= 4:
            return 0.01;
          case dimensionValue > 4:
            return 0.125;
        }
      }
    },
    hollowBarTolerance(dimensionName, dimensionValue) {
      if (dimensionName === "Outside Diameter") {
        return this.roundDimensionTolerance("Diameter", dimensionValue);
      } else if (dimensionName === "Inside Diameter") {
        return -0.25;
      } else if (dimensionName === "Length") {
        switch (true) {
          case dimensionValue <= 0.5:
            return 0.05;
          case dimensionValue <= 0.75:
            return 0.062;
          case dimensionValue <= 1:
            return 0.078;
          case dimensionValue <= 3:
            return 0.093;
          case dimensionValue <= 4:
            return 0.01;
          case dimensionValue > 4:
            return 0.125;
        }
      }
    },
    pipeTolerance(dimensionName, dimensionValue) {
      if (dimensionName === "Wall Thickness") {
        return 1.1;
      } else if (dimensionName === "Outside Diameter") {
        switch (true) {
          case dimensionValue <= 1.5:
            return 0.016;
          case dimensionValue <= 4:
            return 0.031;
          case dimensionValue <= 8:
            return 0.063;
          case dimensionValue <= 18:
            return 0.094;
          case dimensionValue <= 26:
            return 0.125;
          case dimensionValue > 26:
            return 0.156;
        }
      } else if (dimensionName === "Length") {
        switch (true) {
          case dimensionValue <= 0.5:
            return 0.05;
          case dimensionValue <= 0.75:
            return 0.062;
          case dimensionValue <= 1:
            return 0.078;
          case dimensionValue <= 3:
            return 0.093;
          case dimensionValue <= 4:
            return 0.01;
          case dimensionValue > 4:
            return 0.125;
        }
      }
    },
    tubingTolerance(dimensionName, dimensionValue) {
      if (dimensionName === "Wall Thickness") {
        return 1.1;
      } else if (dimensionName === "Outside Diameter") {
        switch (true) {
          case dimensionValue <= 1:
            return 0.004;
          case dimensionValue <= 1.5:
            return 0.005;
          case dimensionValue <= 2:
            return 0.006;
          case dimensionValue <= 2.5:
            return 0.007;
          case dimensionValue <= 3.5:
            return 0.01;
          case dimensionValue > 3.5:
            return 0.02;
        }
      } else if (dimensionName === "Length") {
        switch (true) {
          case dimensionValue <= 0.5:
            return 0.05;
          case dimensionValue <= 0.75:
            return 0.062;
          case dimensionValue <= 1:
            return 0.078;
          case dimensionValue <= 3:
            return 0.093;
          case dimensionValue <= 4:
            return 0.01;
          case dimensionValue > 4:
            return 0.125;
        }
      }
    }
  }
};
</script>
